import * as React from 'react';
import {
    useTable,
    useSortBy,
    useFilters,
    useRowSelect,
    useExpanded,
    useResizeColumns,
    useFlexLayout,
} from 'react-table';

import {getDefault} from './defaults';
import {TableComponent} from './table-component';
import type {TableVariationProps} from './table-component';
import {caseInsensitiveCompare} from './table-sort-helpers';
import type {Column} from './table-types';
import type {SortBy, SpecialState, Row, VirtualizeProps} from './types';

export type Props<T> = TableVariationProps<T> & {
    columns: Column<T>[];
    data: any[];
    sortBy?: SortBy[];
    manualSortBy?: boolean;
    disableSortRemove?: boolean;
    fullWidth?: boolean; // Sets table to full width, proportionally increasing the width of each column,
    extendToFitWidth?: boolean; // Adds an empty cell to the end of each row that extends to fill the remaining width, leaving set column widths as-is,
    onChangeSortBy?: (newSortBy: SortBy[]) => void;
    customCellFunctions?: {
        [key: string]: (...args: any) => any; // These are custom functions that we'll pass to the cell
    };
    specialStates?: SpecialState[];
    firstRow?: React.ReactElement<any> | null | undefined;
    secondRow?: React.ReactElement<any> | null | undefined;
    preserveHeaderInSpecialState?: boolean;
    expandProps?: {
        renderExpandCell: (row: Row<T>) => React.ReactElement;
        renderSubComponent: (row: Row<T>) => React.ReactElement;
        expandRowIndex?: number | null | undefined; // Index of the row to expand,
        clearExpandRowIndex?: () => void; // Used to change the expand row index,
        hideExpandCell?: boolean;
        matchTableWidth?: boolean; // Useful when the table has strict column widths,
        expandAllRows?: boolean; // Used to expand all rows
    };
    virtualizeProps?: VirtualizeProps;
    getTheadRef?: (ref: HTMLTableSectionElement) => void;
};

export function Table<T>(props: Props<T>) {
    const {
        isResizable,
        disableFilters,
        columns,
        disableSorting,
        disableSortRemove,
        data,
        customCellFunctions,
        ...restProps
    } = props;

    const customOptions = customCellFunctions || {};

    const columnData = React.useMemo(
        () =>
            columns.map((column) => ({
                ...getDefault(column.dataType),
                ...column,
            })),
        [columns]
    );

    const resizableDefaultColumn = React.useMemo(
        () => ({
            minWidth: 70,
            width: 150,
            maxWidth: 400,
        }),
        []
    );

    const sortTypes = React.useMemo(
        () => ({
            alphanumeric: caseInsensitiveCompare,
        }),
        []
    );

    const plugins = [useFilters, useSortBy, useRowSelect, useFlexLayout];
    const options = {
        columns: columnData,
        data: data,
        disableFilters: disableFilters,
        disableSortBy: disableSorting,
        // Disabling "sortRemove" turns off triple-sort and instead
        // forces every column that's sorted to either by descending
        // or ascending (no third option of "no sort")
        disableSortRemove: disableSortRemove,
        initialState: (() => {
            const initial: {sortBy?: SortBy[]; selectedRowIds?: Record<string, boolean>} = {};
            if (props.sortBy) {
                initial.sortBy = props.sortBy;
            }

            if (props.defaultAllRowsChecked) {
                const selectedRowIds: Record<string, boolean> = {};
                for (let i = 0; i < data.length; i++) {
                    selectedRowIds[String(i)] = true;
                }
                initial.selectedRowIds = selectedRowIds;
            }

            return Object.keys(initial).length > 0 ? initial : undefined;
        })(),
        defaultColumn: undefined,
        manualSortBy: props.manualSortBy,
        sortTypes,
        ...customOptions,
    } as const;

    if (props.expandProps) {
        // @ts-expect-error
        plugins.push(useExpanded);
    }

    if (isResizable) {
        // @ts-expect-error
        plugins.push(useResizeColumns);
        // @ts-expect-error
        options.defaultColumn = resizableDefaultColumn;
    }

    // @ts-expect-error
    const instanceProps = useTable(options, ...plugins);

    React.useEffect(() => {
        if (props.onCheckedRowsChange) {
            // @ts-expect-error
            props.onCheckedRowsChange(instanceProps.selectedFlatRows);
        }
        // Depend on reducer state (`selectedRowIds`), not `selectedFlatRows`: the latter's array identity
        // can change when `rows` is rebuilt (data/sort/filter) even if the same IDs stay selected.
        // @ts-expect-error
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instanceProps.state.selectedRowIds]);

    // @ts-expect-error
    return <TableComponent {...instanceProps} {...restProps} />;
}
